import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import type { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import type { PageData } from 'api/Pagination/Types'
import type {
  PaymentProviderGroupDto,
  UpdatePaymentProviderGroupDto,
} from 'api/PaymentProvider/Types/PaymentProviderGroupDto'
import { PSPGroupTypes, PSPMethodTypes } from 'api/PaymentProvider/Types/PaymentProviderGroupDto'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { getAllowedPaymentProviderFilterFields } from 'components/Filter/allowedPaymentProviderFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { FilterCell } from 'components/Filter/FilterCell'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'
import { buildQuery, buildSortQuery, FilterLogic } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import ClientAreaAuditLogModal from '../../../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import {
  AdministrationAllowedPaymentProvidersNavigation,
  AdministrationAllowedPaymentProvidersTab,
} from './AdministrationPaymentProvidersNavigation'
import EditPaymentProviderPage from './EditPaymentProviderPage'
import { normalizePaymentProviders } from './normalizer/normalizePaymentProviders'
import { normalizePaymentProvidersSortQuery } from './normalizer/normalizePaymentProvidersSortQuery'
import { PaymentProvidersFilterKey } from './PaymentProvidersFilters'
import PaymentProvidersMatrixColumns from './PaymentProvidersMatrixColumns'
import PaymentProvidersUncoveredColumns from './PaymentProvidersUncoveredColumns'

interface AllowedPaymentProvidersProps {
  uncovered?: boolean
}

export const AllowedPaymentProviders: React.FC<AllowedPaymentProvidersProps> = ({ uncovered }) => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [paymentProviderGroups, setPaymentProviderGroups] = useState<PaymentProviderGroupDto[]>([])
  const [paymentProvider, setPaymentProvider] = useState<PaymentProviderGroupDto | null>(null)
  const [isModalOpen, setModalOpen] = useState(false)
  const [groupToRemove, setGroupToRemove] = useState<{
    data?: PaymentProviderGroupDto
    showModal: boolean
  }>()
  const [auditLogModal, setAuditLogModal] = useState<{
    visible: boolean
    data?: PaymentProviderGroupDto
  }>({
    visible: false,
  })

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const PaymentProvidersColumns = useMemo(
    () => (uncovered ? PaymentProvidersUncoveredColumns : PaymentProvidersMatrixColumns),
    [uncovered]
  )

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const getSearchQuery = useCallback(
    (filter?: CompositeFilterDescriptor): string | null => {
      const defaultFilters = {
        logic: FilterLogic.OR,
        filters: [
          {
            field: PaymentProvidersFilterKey.PspGroupType,
            operator: '==',
            value: uncovered ? PSPGroupTypes.Uncovered : PSPGroupTypes.DepositWithdraw,
          },
        ],
      }

      const base = filter ?? gridState.filter
      if (!base) return buildQuery({ logic: FilterLogic.AND, filters: [], defaultFilters }) ?? null

      return buildQuery({ ...base, defaultFilters }) ?? null
    },
    [gridState.filter, uncovered]
  )

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null =>
      buildSortQuery(normalizePaymentProvidersSortQuery(sortDescriptors ?? gridState.sort)),
    [gridState.sort]
  )

  const applyResult = useCallback(
    (response: PageData<PaymentProviderGroupDto[]>) => {
      const raw = response?.items ?? []
      const normalized = normalizePaymentProviders(raw, uncovered)

      const filtered = normalized.filter(
        (item) =>
          item.pspGroupType.id ===
          (uncovered ? PSPGroupTypes.Uncovered : PSPGroupTypes.DepositWithdraw)
      )

      setPaymentProviderGroups(filtered)

      const total = response.totalResults ?? 0
      if (gridState.total !== total) {
        gridActions.setGridFilters({ total })
      }

      setTableData((prev) => ({ ...prev, meta: response }))
    },
    [gridActions, gridState.total, uncovered]
  )

  // Single fetch path: call this from mount + handlers only
  const fetchAndApply = useCallback(
    async (opts?: {
      search?: string | null
      sort?: string | null
      pageIndex?: number
      pageSize?: number
      skipCount?: boolean
    }) => {
      gridStatusActions.setLoading(true)
      try {
        const pageIndex = opts?.pageIndex ?? gridState.pageIndex ?? 1
        const pageSize = opts?.pageSize ?? gridState.take ?? gridState.pageSize ?? 10
        const skipCount = opts?.skipCount ?? tableData?.skipCount !== false

        const response = await walletsApiClient.getPaymentProvidersGroups({
          search: opts?.search ?? getSearchQuery(gridState.filter),
          sort: opts?.sort ?? getSort(gridState.sort),
          pageIndex,
          pageSize,
          skipCount,
        })

        applyResult(response)

        // keep pagination in sync (minimal patch)
        gridActions.setGridFilters({
          pageIndex,
          take: pageSize,
          pageSize,
          skip: (pageIndex - 1) * pageSize,
        })
      } finally {
        gridStatusActions.setLoading(false)
      }
    },
    [
      applyResult,
      getSearchQuery,
      getSort,
      gridActions,
      gridState.filter,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      gridStatusActions,
      tableData?.skipCount,
      walletsApiClient,
    ]
  )

  // initial fetch (mount + uncovered change)
  React.useEffect(() => {
    const initial = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )
    gridActions.setGridFilters(initial)

    void fetchAndApply({
      pageIndex: initial.pageIndex ?? 1,
      pageSize: (initial.take ??
        initial.pageSize ??
        gridState.take ??
        gridState.pageSize ??
        10) as number,
      search: getSearchQuery(initial.filter),
      sort: getSort(initial.sort),
      skipCount: tableData?.skipCount !== false,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uncovered])

  // Used by SelectFiltersFacade
  const filterFetch = useCallback(
    async (params?: { query?: string | null }) => {
      await fetchAndApply({
        pageIndex: 1,
        pageSize: gridState.take ?? gridState.pageSize ?? 10,
        search: params?.query !== undefined ? params.query : getSearchQuery(gridState.filter),
        sort: getSort(gridState.sort),
      })
    },
    [
      fetchAndApply,
      getSearchQuery,
      getSort,
      gridState.filter,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
    ]
  )

  const onSubmit = async (values: UpdatePaymentProviderGroupDto) => {
    if (values.pspGroupId) {
      await walletsApiClient.updatePaymentProvidersGroup(values.pspGroupId, values)
      setToast(successToast('PSP Group updated.'))
    } else if (
      values.groupMethods.some(
        (m) =>
          m.methodTypeId === PSPMethodTypes.Deposit || m.methodTypeId === PSPMethodTypes.Withdraw
      )
    ) {
      await walletsApiClient.createPaymentProvidersGroup({
        ...values,
        pspGroupTypeId: PSPGroupTypes.DepositWithdraw,
      })
      setToast(successToast('PSP Group added.'))
    } else {
      await walletsApiClient.createPaymentProvidersGroup({
        ...values,
        pspGroupTypeId: PSPGroupTypes.Uncovered,
      })
      setToast(successToast('PSP Group added.'))
    }

    setPaymentProvider(null)
    setModalOpen(false)
    await fetchAndApply()
  }

  const handleEdit = (dto?: PaymentProviderGroupDto) => {
    setPaymentProvider(dto ?? null)
    setModalOpen(true)
  }

  const handleRemove = async () => {
    try {
      if (groupToRemove?.data)
        await walletsApiClient.removePaymentProvidersGroup(groupToRemove.data.id)
      setToast(successToast('PSP Group removed.'))
      await fetchAndApply()
    } finally {
      setGroupToRemove((prev) => (prev ? { ...prev, showModal: false } : prev))
    }
  }

  const handleSkipCountChange = async (skipCount: boolean) => {
    setTableData((prev) => ({ ...prev, skipCount }))
    await fetchAndApply({ skipCount })
  }

  const handleGridPageChange = async (page: number) => {
    await fetchAndApply({ pageIndex: page })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await fetchAndApply({ pageIndex: 1, pageSize })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({ sort: event.sort })
    await fetchAndApply({ sort: getSort(event.sort) })
  }

  const setAuditLog = (dto: PaymentProviderGroupDto) =>
    setAuditLogModal({ data: dto, visible: true })

  if (gridStatusState.isLoading) return <Spinner />

  return (
    <>
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          pspGroupMethodId={auditLogModal.data.id}
        />
      )}

      {isModalOpen && (
        <EditPaymentProviderPage
          data={paymentProvider ?? undefined}
          onCloseModal={() => setModalOpen(false)}
          onSubmit={onSubmit}
          uncovered={uncovered}
        />
      )}

      <ConfirmationModal
        contentText={`Are you sure you want to delete the following PSP Group: ${groupToRemove?.data?.name}`}
        yesButtonText='Yes, delete'
        onYes={handleRemove}
        onCancel={() => setGroupToRemove((prev) => (prev ? { ...prev, showModal: false } : prev))}
        visible={!!groupToRemove?.showModal}
      />

      <Breadcrumbs
        crumbs={[
          { title: "Allowed PSP's", link: links.administrationAllowedPaymentProvidersMatrix.url },
        ]}
      />

      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.AllowedPaymentProviders}
        filterFields={getAllowedPaymentProviderFilterFields()}
        gridState={gridState}
        getQuery={getSearchQuery} // ✅ IMPORTANT: inject default PSPGroupType filter into saved-filter selection
      />

      <AddButton
        disabled={
          (paymentProviderGroups.length > 0 && uncovered) ||
          !hasPermission(permissions, PermissionsEnum.CanEditPspGroups)
        }
        text='Add PSP Group'
        onClick={() => handleEdit()}
        data-test='add-psp-group-button'
      />

      <AdministrationAllowedPaymentProvidersNavigation
        tab={
          uncovered
            ? AdministrationAllowedPaymentProvidersTab.UNCOVERED_CARD_DEPOSITS
            : AdministrationAllowedPaymentProvidersTab.PSP_MATRIX
        }
      />

      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={paymentProviderGroups}
          sort={gridState.sort}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          onSortChange={handleSortChange}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
          style={{ zIndex: 0 }}
        >
          {PaymentProvidersColumns.filter((c) => c.show !== false).map((column, idx) => (
            <Column key={idx} {...column} />
          ))}

          {(hasPermission(permissions, PermissionsEnum.CanEditPspGroups) ||
            hasPermission(permissions, PermissionsEnum.CanDeletePspGroups)) && (
            <Column
              width={50}
              title=''
              filterCell={FilterCell}
              cell={(props: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        disabled: !hasPermission(permissions, PermissionsEnum.CanEditPspGroups),
                        onClick: () => handleEdit(props.dataItem),
                      },
                      {
                        name: 'Remove',
                        'data-test': 'remove',
                        disabled: !hasPermission(permissions, PermissionsEnum.CanDeletePspGroups),
                        onClick: () =>
                          setGroupToRemove({
                            data: props.dataItem,
                            showModal: true,
                          }),
                      },
                      {
                        name: 'Audit Log',
                        onClick: () => setAuditLog(props.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          )}
        </Grid>
      </TableStateContext.Provider>
    </>
  )
}
